import React, { useState, useRef, useEffect, useCallback } from 'react';
import ARTracker from '../../classes/ARTracker';
import TextUtils from '../../classes/utilities/TextUtils';
import ProjectSaver from '../../classes/ProjectSaver';
import { LayoutOrientations, SaveModificationTypes } from '../../globals';
import Button from '../layout/Button';
import Panel from '../layout/Panel';
import SearchField from '../layout/SearchField';
import Spacer from '../layout/Spacer';
import ListView from '../listComponent/ListView';
import ListItem from '../listComponent/ListItem';
import { IListNodeProps } from '../../interfaces/List';
import useForceUpdate from '../reactUtils/useForceUpdate';
import { getUuidv4 } from '../../classes/utilities/Misc';

interface IARTrackerPickerProps
{
	/** the guid of the currently selected scene object */
	value: string;
	/** a callback function that will be executed when any of the entries in the list is selected by the user, with the guid of the selected object as the parameter. *optional* */
	onSelectedCallback: (selected: string) => void;
	/** a callback function that will be executed when any of the entries in the list is selected by the user, passing no parameters. this will mostly be used when showing the picker through a modal dialog. *optional* */
	onShutdown?: () => void;
}

export default function ARTrackerPicker(props: IARTrackerPickerProps)
{
	const forceUpdate = useForceUpdate();
	
	const [selectedObjectId, setSelectedObjectId] = useState(props.value);
	const [searchQuery, setSearchQuery] = useState("");
	const [guidSet, setGuidSet] = useState(new Set());
	const [trackerList, setTrackerList] = useState<Array<ARTracker>>([]);
	
	const selfRef = useRef(null);
	const guid = useRef(getUuidv4());
	const previouslyFocused = useRef(document.activeElement);
	
	useEffect(() => 
	{
		setTrackerList(ARTracker.getTrackerList());
	}, []);

	useEffect(() =>
	{
		if (selfRef.current)
		{
			// document.getElementById(guid.current).focus();
			//For some reason it doesn't focus when done here, so delay it to the next event loop cycle...
			const o = document.getElementById(guid.current);
			setTimeout(() =>{ o?.focus() }, 0);
		}
		
		if(selectedObjectId)
		{
			const guidSet = new Set();
			guidSet.add(selectedObjectId);
			setGuidSet(guidSet);
		}
	
		return () =>
		{
			if (previouslyFocused.current)
			{
				(previouslyFocused.current as HTMLElement).focus();
			}
		}
	}, [selfRef.current]);

	const getTrackerObjectById = useCallback((id: string) =>
	{
		for(const tracker of trackerList)
		{
			if(tracker.userData.guid === id)
				return tracker;
		}

		return null;
	}, [trackerList]);

	function onObjectSelected(selectedItems: Set<IListNodeProps>)
	{
		const value = selectedItems.values().next().value;
		if(value)
		{
			setSelectedObjectId(value.nodeId);
			setGuidSet(new Set().add(value.nodeId));
		}
		else
		{
			setSelectedObjectId("");
		}

		forceUpdate();
	}

	const onObjectPicked = useCallback(() =>
	{
		if (props.onSelectedCallback)
		{
			props.onSelectedCallback(selectedObjectId);
			if(selectedObjectId)
			{
				const tracker = getTrackerObjectById(selectedObjectId);
				if(tracker)
				{
					ProjectSaver.getInstance().markModifiedObject(tracker, SaveModificationTypes.Reference);
				}
			}
		}

		props.onShutdown?.();
	}, [selectedObjectId, props.onSelectedCallback, props.onShutdown]);

	return (
		<Panel orientation={LayoutOrientations.Horizontal}>
			<Panel className="solid composite"
				ref={selfRef}		
				style={{
					maxHeight: "500px",
					maxWidth: "500px",
				}}
			>
				<SearchField
					onChangeCallback={(v) => setSearchQuery(v)} 
					id={guid.current}
					value={searchQuery}
				/>
				<Spacer size={{ y: "2px" }} />
				<ListView
					selectedItems={guidSet}
					selectionChangedCallback={onObjectSelected}
					doubleClickCallback={onObjectPicked}
					dndDefaults={{
						dragEnabled: false,
						dropEnabled: false,
					}}
				>
					<ListItem
						key={"none"}
						nodeId={null}
						label={" - None - "}
					/>
					{
						trackerList.map((tracker) =>
						{
							if(TextUtils.searchString(tracker.name || tracker.type, searchQuery))
							{
								return (
									<ListItem 
										key={tracker.userData.guid}
										nodeId={tracker.userData.guid}
										label={tracker.name}
									/>
								);
							}
						}).filter((v) => !!v)
					}
				</ListView>
				<Button
					category={"AR Trackers"}
					label={"Confirm"}
					onClick={onObjectPicked}
				/>
			</Panel>
		</Panel>	
	);
}